import React from 'react'
import { Service, ValidationError, api } from '../../api/client'
import type { Package } from '../../types/app'
import { formatPrice, parsePrice } from '../../catalog/format'
import { savingPence } from '../../packages/pricing'
import Field from './Field'
import LengthChecklist from './LengthChecklist'
import { useDialog } from '../../hooks/useDialog'
import {
  PackageForm,
  applyDiscount,
  applyPrice,
  priceChangeNote,
  savingRange,
  singleLengthUnit,
  toPackageForm,
  toPackageInput,
  type ValidityUnit,
} from './packageForm'
import { saveErrorMessage } from './toast'
import '../../components/Sheet.css'
import './Settings.css'

interface PackageSheetProps {
  pkg: Package | null
  services: Service[]
  onClose: () => void
  onSaved: (message: string) => void
}

export default function PackageSheet({ pkg, services, onClose, onSaved }: PackageSheetProps) {
  const [form, setForm] = React.useState<PackageForm>(() => toPackageForm(pkg, services))
  const [errors, setErrors] = React.useState<Record<string, string>>({})
  const [busy, setBusy] = React.useState(false)
  const dialogRef = React.useRef<HTMLDivElement>(null)
  useDialog(dialogRef, onClose, { closable: !busy })
  const [error, setError] = React.useState('')
  const [confirmDelete, setConfirmDelete] = React.useState(false)

  const set = <K extends keyof PackageForm>(key: K, value: PackageForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }))

  const unit = singleLengthUnit(form.lengthIds, services)
  const singleService = form.lengthIds.length === 1

  const toggleLength = (lengthId: string) => {
    const nextIds = form.lengthIds.includes(lengthId)
      ? form.lengthIds.filter((id) => id !== lengthId)
      : [...form.lengthIds, lengthId]
    // Changing the selection changes the baseline a percentage means, so
    // re-derive the discount from the price that's already on screen rather
    // than leaving a figure that referred to the old baseline.
    const nextUnit = singleLengthUnit(nextIds, services)
    setForm(applyPrice({ ...form, lengthIds: nextIds }, form.price, nextUnit ?? 0))
  }

  const onDiscountChange = (value: string) => {
    setForm(applyDiscount(form, value, unit ?? 0))
  }

  const onPriceChange = (value: string) => {
    setForm(applyPrice(form, value, unit ?? 0))
  }

  const note = pkg ? priceChangeNote(pkg, services) : null

  const sessions = Number.parseInt(form.sessions, 10)
  const pricePence = parsePrice(form.price)

  const savingText = (() => {
    if (Number.isNaN(sessions) || sessions <= 0 || pricePence === null) {
      return null
    }
    if (singleService && unit !== null) {
      return formatPrice(savingPence(sessions, unit, pricePence))
    }
    const range = savingRange(sessions, form.lengthIds, services, pricePence)
    if (!range) {
      return null
    }
    return range.min === range.max
      ? formatPrice(range.min)
      : `${formatPrice(range.min)} – ${formatPrice(range.max)}`
  })()

  const save = async (e: React.FormEvent) => {
    e.preventDefault()
    const result = toPackageInput(form, services)
    if ('errors' in result) {
      setErrors(result.errors)
      return
    }
    setBusy(true)
    setErrors({})
    setError('')
    try {
      if (pkg) {
        await api.updatePackage(pkg.id, result.input, pkg.version)
        onSaved(`${result.input.name} saved`)
      } else {
        await api.createPackage(result.input)
        onSaved(`${result.input.name} added`)
      }
    } catch (err) {
      if (err instanceof ValidationError) {
        setErrors(err.fields)
      } else {
        setError(saveErrorMessage(err))
      }
      setBusy(false)
    }
  }

  const remove = async () => {
    if (!pkg) {
      return
    }
    if (!confirmDelete) {
      setConfirmDelete(true)
      return
    }
    setBusy(true)
    setError('')
    try {
      await api.deletePackage(pkg.id)
      onSaved(`${pkg.name} deleted`)
    } catch (err) {
      setError(saveErrorMessage(err))
      setBusy(false)
    }
  }

  return (
    <div className="sheet-overlay settings-sheet" onClick={() => { if (!busy) onClose() }}>
      <div ref={dialogRef}
        className="sheet-content"
        role="dialog"
        aria-modal="true"
        aria-label={pkg ? `Edit ${pkg.name}` : 'Add a package'}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sheet-handle"></div>
        <div className="sheet-header">
          <h2>{pkg ? 'Edit package' : 'New package'}</h2>
        </div>

        <form className="settings-form sheet-form" onSubmit={save} noValidate>
          <Field id="package-name" label="Name" error={errors.name}>
            <input id="package-name" value={form.name} onChange={(e) => set('name', e.target.value)} />
          </Field>

          <Field id="package-description" label="Description (optional)" error={errors.description}>
            <textarea
              id="package-description"
              rows={2}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
            />
          </Field>

          <Field id="package-services" label="Treatments" error={errors.service_ids ?? errors.service_length_ids}>
            {services.length === 0 && (
              <p className="field-hint">Add a treatment to your menu first, then choose it here.</p>
            )}
            <LengthChecklist
              id="package-services"
              label="Treatments"
              services={services}
              ticked={form.lengthIds}
              onToggle={toggleLength}
            />
          </Field>

          <Field id="package-sessions" label="Sessions" error={errors.session_count}>
            <input
              id="package-sessions"
              type="number"
              inputMode="numeric"
              min={1}
              value={form.sessions}
              onChange={(e) => set('sessions', e.target.value)}
            />
          </Field>
          <Field id="package-validity" label="Valid for (optional)" error={errors.validity_months ?? errors.validity_days}>
            <div className="validity-input">
              <input
                id="package-validity"
                type="number"
                inputMode="numeric"
                min={1}
                value={form.validity}
                onChange={(e) => set('validity', e.target.value)}
              />
              <select
                aria-label="Months or days"
                value={form.validityUnit}
                onChange={(e) => set('validityUnit', e.target.value as ValidityUnit)}
              >
                <option value="months">months</option>
                <option value="days">days</option>
              </select>
            </div>
          </Field>
          <div className="field-pair">
            <Field id="package-price" label="Price (£)" error={errors.price_pence}>
              <input
                id="package-price"
                inputMode="decimal"
                value={form.price}
                onChange={(e) => onPriceChange(e.target.value)}
              />
            </Field>
            <Field
              id="package-discount"
              label="Discount (%)"
              hint={singleService ? undefined : 'Choose one length to set a discount.'}
            >
              <input
                id="package-discount"
                inputMode="decimal"
                placeholder="10"
                disabled={!singleService}
                value={form.discount}
                onChange={(e) => onDiscountChange(e.target.value)}
              />
            </Field>
          </div>

          {savingText && <p className="field-hint">Saving: {savingText}.</p>}
          {note && <p className="field-hint">{note}</p>}

          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={form.sellOnline}
              onChange={(e) => set('sellOnline', e.target.checked)}
            />
            Clients can buy this online
          </label>
          {form.sellOnline && pricePence === 0 && (
            <p className="field-hint">Free packages are never sold online.</p>
          )}

          {error && <p className="field-error" role="alert">{error}</p>}

          <div className="sheet-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>

          {pkg && (
            <button type="button" className="btn btn-danger btn-compact" onClick={remove} disabled={busy}>
              {confirmDelete ? `Tap again to delete ${pkg.name}` : 'Delete package'}
            </button>
          )}
        </form>
      </div>
    </div>
  )
}
